import type { StaffRole } from '@planuze/admin-contracts';
import {
  Breadcrumb,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  InlineAlert,
  Page,
  PageContent,
  PageDescription,
  PageHeader,
  PageTitle,
  PageTitleBlock,
  toast,
  WizardStepper,
  type WizardStepperItem,
} from '@planuze/ui';
import { useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { CapabilitiesEditor } from '../components/plans/capabilities-editor.js';
import { planCatalogErrorMessage } from '../components/plans/plan-error.js';
import {
  PublicIdentityFields,
  PublicPricingFields,
} from '../components/plans/plan-form-fields/public-fields.js';
import { SeatFields } from '../components/plans/plan-form-fields/seat-fields.js';
import { TargetingFields } from '../components/plans/plan-form-fields/targeting-fields.js';
import {
  type TargetedPlanCreateContent,
  useTargetedPlanCreateContent,
} from '../content/sections/targeted-plan-create.js';
import { type PlanForm, usePlanForm } from '../hooks/use-plan-form.js';
import { useCreatePlan } from '../hooks/use-plans.js';
import { useStaffSession } from '../hooks/use-staff-session.js';
import { formatCents } from '../lib/currency.js';

const MANAGE_ROLES: readonly StaffRole[] = ['super_admin', 'billing_ops'];

type Step = 'form' | 'review';

/* Criação de plano NEGOCIADO em página cheia com etapa de revisão (ADR-0704). Diferente do plano
   público, o negociado é um plano COMPLETO direcionado (alvo org XOR usuário + validade + motivo +
   herança de acesso). Criar dispara Product + Price no Stripe (IRREVERSÍVEL) → passo 1 monta o
   formulário completo, passo 2 mostra a revisão read-only antes de confirmar. Só
   billing_ops/super_admin (canManage). */
export function TargetedPlanCreatePage() {
  const navigate = useNavigate();
  const session = useStaffSession();
  const copy = useTargetedPlanCreateContent();
  const canManage = MANAGE_ROLES.includes(session?.role ?? 'readonly');
  const form = usePlanForm({ targeted: true });
  const create = useCreatePlan();
  const busy = create.isPending;
  const [step, setStep] = useState<Step>('form');
  const steps: readonly WizardStepperItem[] = [
    { id: 'form', label: copy.stepForm },
    { id: 'review', label: copy.stepReview },
  ];

  const goToList = (): void => {
    navigate({ to: '/plans' }).catch(() => undefined);
  };

  if (!canManage) {
    return (
      <Page>
        <PageHeader>
          <PageTitleBlock>
            <Breadcrumb
              ariaLabel={copy.breadcrumbAriaLabel}
              items={[
                { label: copy.breadcrumbPlans, onClick: goToList },
                { label: copy.pageTitle },
              ]}
            />
            <PageTitle>{copy.pageTitle}</PageTitle>
          </PageTitleBlock>
        </PageHeader>
        <PageContent>
          <InlineAlert variant="destructive">{copy.noPermission}</InlineAlert>
        </PageContent>
      </Page>
    );
  }

  const submit = (): void => {
    if (!form.valid) return;
    create.mutate(form.createInput, {
      onSuccess: () => {
        toast.success(copy.createdToast);
        goToList();
      },
      onError: (err) => toast.error(planCatalogErrorMessage(err)),
    });
  };

  return (
    <Page>
      <PageHeader>
        <PageTitleBlock>
          <Breadcrumb
            ariaLabel={copy.breadcrumbAriaLabel}
            items={[{ label: copy.breadcrumbPlans, onClick: goToList }, { label: copy.pageTitle }]}
          />
          <PageTitle>{copy.pageTitle}</PageTitle>
          <PageDescription>{copy.pageDescription}</PageDescription>
        </PageTitleBlock>
      </PageHeader>
      <PageContent className="space-y-6">
        <WizardStepper
          steps={steps}
          currentIndex={step === 'form' ? 0 : 1}
          ariaLabel={copy.stepperAriaLabel}
        />
        {step === 'form' ? (
          <TargetedPlanForm
            form={form}
            copy={copy}
            onCancel={goToList}
            onReview={() => setStep('review')}
          />
        ) : (
          <TargetedPlanReview
            form={form}
            copy={copy}
            busy={busy}
            onBack={() => setStep('form')}
            onConfirm={submit}
          />
        )}
      </PageContent>
    </Page>
  );
}

/* Passo 1 — formulário completo. Reusa os grupos de campos extraídos (ADR-0704): identidade + preço
   (o switch "Público no app" some sozinho no modo direcionado), direcionamento/herança, assentos
   (só plano de org) e capabilities. Só avança p/ a revisão quando `form.valid`. */
function TargetedPlanForm({
  form,
  copy,
  onCancel,
  onReview,
}: {
  readonly form: PlanForm;
  readonly copy: TargetedPlanCreateContent;
  readonly onCancel: () => void;
  readonly onReview: () => void;
}) {
  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>{copy.form.identityTitle}</CardTitle>
          <CardDescription>{copy.form.identityDescription}</CardDescription>
        </CardHeader>
        <CardContent>
          <PublicIdentityFields form={form} />
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <CardTitle>{copy.form.priceTitle}</CardTitle>
          <CardDescription>{copy.form.priceDescription}</CardDescription>
        </CardHeader>
        <CardContent>
          <PublicPricingFields form={form} />
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <CardTitle>{copy.form.targetingTitle}</CardTitle>
          <CardDescription>{copy.form.targetingDescription}</CardDescription>
        </CardHeader>
        <CardContent>
          <TargetingFields form={form} />
        </CardContent>
      </Card>
      {form.isOrgPlan && (
        <Card>
          <CardHeader>
            <CardTitle>{copy.form.seatsTitle}</CardTitle>
            <CardDescription>{copy.form.seatsDescription}</CardDescription>
          </CardHeader>
          <CardContent>
            <SeatFields form={form} />
          </CardContent>
        </Card>
      )}
      <Card>
        <CardHeader>
          <CardTitle>{copy.form.capabilitiesTitle}</CardTitle>
          <CardDescription>{copy.form.capabilitiesDescription}</CardDescription>
        </CardHeader>
        <CardContent>
          <CapabilitiesEditor
            value={form.capabilities}
            onChange={form.setCapabilities}
            allowSeats={false}
          />
        </CardContent>
      </Card>
      <div className="flex justify-end gap-2">
        <Button variant="ghost" onClick={onCancel}>
          {copy.form.cancel}
        </Button>
        <Button disabled={!form.valid} onClick={onReview}>
          {copy.form.review}
        </Button>
      </div>
    </>
  );
}

/* Passo 2 — revisão read-only do que será criado. O Product + Price no Stripe é irreversível, então
   o alvo/validade/preço/capabilities são exibidos para conferência antes de confirmar. */
function TargetedPlanReview({
  form,
  copy,
  busy,
  onBack,
  onConfirm,
}: {
  readonly form: PlanForm;
  readonly copy: TargetedPlanCreateContent;
  readonly busy: boolean;
  readonly onBack: () => void;
  readonly onConfirm: () => void;
}) {
  const targetLabel =
    form.targetType === 'organization' ? copy.review.organization : copy.review.user;
  const targetValue =
    form.targetType === 'organization' ? (form.org?.name ?? '—') : (form.user?.email ?? '—');
  const caps = Object.entries(form.effectiveCapabilities);

  return (
    <>
      <InlineAlert variant="warning">{copy.review.irreversibleWarning}</InlineAlert>
      <Card>
        <CardHeader>
          <CardTitle>{copy.form.identityTitle}</CardTitle>
        </CardHeader>
        <CardContent>
          <dl className="grid grid-cols-2 gap-3">
            <ReviewRow label={copy.review.tier} value={form.tier} />
            <ReviewRow label={copy.review.rank} value={String(form.rank)} />
            <ReviewRow label={copy.review.lookupKey} value={form.planKey} />
            <ReviewRow label={copy.review.displayName} value={form.displayName} />
          </dl>
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <CardTitle>{copy.form.priceTitle}</CardTitle>
        </CardHeader>
        <CardContent>
          <dl className="grid grid-cols-2 gap-3">
            <ReviewRow
              label={form.isOrgPlan ? copy.review.basePrice : copy.form.priceTitle}
              value={formatCents(form.priceCents, 'USD')}
            />
            <ReviewRow
              label={copy.review.interval}
              value={form.interval === 'month' ? copy.review.monthly : copy.review.yearly}
            />
            {form.isOrgPlan && (
              <>
                <ReviewRow label={copy.review.includedSeats} value={String(form.includedSeats)} />
                <ReviewRow
                  label={copy.review.extraSeatPrice}
                  value={formatCents(form.extraSeatPriceCents, 'USD')}
                />
              </>
            )}
          </dl>
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <CardTitle>{copy.form.targetingTitle}</CardTitle>
        </CardHeader>
        <CardContent>
          <dl className="grid grid-cols-2 gap-3">
            <ReviewRow label={copy.review.targetType} value={targetLabel} />
            <ReviewRow label={copy.review.target} value={targetValue} />
            <ReviewRow label={copy.review.validFrom} value={form.validFrom} />
            <ReviewRow
              label={copy.review.validUntil}
              value={form.validUntil.length > 0 ? form.validUntil : '—'}
            />
            <div className="col-span-2">
              <ReviewRow label={copy.review.reason} value={form.reason} />
            </div>
            <div className="col-span-2">
              <ReviewRow
                label={copy.review.inheritsFrom}
                value={
                  form.inheritedPlanKeys.length > 0
                    ? form.inheritedPlanKeys.join(', ')
                    : copy.review.none
                }
              />
            </div>
          </dl>
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <CardTitle>{copy.form.capabilitiesTitle}</CardTitle>
        </CardHeader>
        <CardContent>
          {caps.length === 0 ? (
            <p className="text-sm text-muted-foreground">{copy.review.noCapabilitiesGranted}</p>
          ) : (
            <ul className="space-y-1.5">
              {caps.map(([key, value]) => (
                <li key={key} className="flex items-center justify-between gap-3 text-sm">
                  <span className="text-foreground">{key}</span>
                  <span className="text-muted-foreground">
                    {value === true ? copy.review.yes : String(value)}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
      <div className="flex justify-end gap-2">
        <Button variant="ghost" onClick={onBack} disabled={busy}>
          {copy.review.back}
        </Button>
        <Button loading={busy} disabled={!form.valid} onClick={onConfirm}>
          {copy.review.confirm}
        </Button>
      </div>
    </>
  );
}

function ReviewRow({ label, value }: { readonly label: string; readonly value: string }) {
  return (
    <div className="space-y-0.5">
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="text-sm text-foreground">{value.length > 0 ? value : '—'}</dd>
    </div>
  );
}
